import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

import { type ControlSize } from './input';
import { cn } from './utils';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Visual height. This intentionally replaces the native numeric `size` attribute. */
  size?: ControlSize;
  invalid?: boolean;
  placeholder?: ReactNode;
}

const sizeClasses: Record<ControlSize, string> = {
  sm: 'min-h-9 rounded-lg py-1.5 pl-3 pr-9 text-sm',
  md: 'min-h-10 rounded-xl py-2 pl-3.5 pr-10 text-sm',
  lg: 'min-h-12 rounded-xl py-2.5 pl-4 pr-11 text-base',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { children, className, invalid = false, placeholder, required, size = 'md', ...props },
  ref,
) {
  return (
    <div className="relative">
      <select
        {...props}
        ref={ref}
        aria-invalid={invalid || undefined}
        required={required}
        className={cn(
          'block w-full appearance-none border bg-white font-normal text-slate-950 shadow-sm shadow-slate-950/[0.02] outline-none transition disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
          sizeClasses[size],
          invalid
            ? 'border-rose-400 bg-rose-50/30 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10'
            : 'border-slate-200 hover:border-slate-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10',
          className,
        )}
      >
        {placeholder ? (
          <option disabled={required} value="">
            {placeholder}
          </option>
        ) : null}
        {children}
      </select>
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-slate-500"
        fill="none"
        viewBox="0 0 16 16"
      >
        <path
          d="m4 6 4 4 4-4"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.75"
        />
      </svg>
    </div>
  );
});

Select.displayName = 'Select';
